import React, { useState } from 'react'
import { useCookieConsent, COOKIE_CATEGORIES } from '../contexts/CookieConsentContext'
import PropTypes from 'prop-types'

/**
 * @typedef {import('../contexts/CookieConsentContext').ConsentPrefs} ConsentPrefs
 */

export const CookieDetailsModal = ({ onClose }) => (
  <div className="fixed inset-0 z-60 bg-black bg-opacity-50 flex items-center justify-center p-4">
    <div className="bg-white rounded-lg p-6 w-full max-w-md">
      <h3 className="text-lg font-semibold mb-4">Our Cookies & Scripts</h3>
      <ul className="list-disc list-inside space-y-2 text-sm">
        <li><strong>Necessary:</strong> Session IDs, CSRF tokens, Checkout and Payment processing</li>
        <li><strong>Analytics:</strong> Google Analytics, Core Web Vitals reporting</li>
        <li><strong>Marketing:</strong> Klaviyo, Facebook Pixel</li>
        <li><strong>Advertising:</strong> Google Ads, Bing UET, Criteo, Third‑party ad networks</li>
        <li><strong>Live Chat:</strong> Intercom (chat transcripts, user profile data)</li>
      </ul>
      <div className="mt-6 text-right">
        <button onClick={onClose} className="btn btn-primary">
          Close
        </button>
      </div>
    </div>
  </div>
)
CookieDetailsModal.propTypes = { onClose: PropTypes.func }

export const CookieSettingsModal = ({ onClose }) => {
  const { prefs, setPrefs } = useCookieConsent()
  const [localPrefs, setLocalPrefs] = useState(prefs)
  const [detailsOpen, setDetailsOpen] = useState(false)

  const saveAndClose = () => {
    setPrefs(localPrefs)
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="absolute z-[-1] inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 cursor-not-allowed" onClick={onClose} />

      <div className="bg-white rounded-lg p-6 w-full max-w-md text-gray-900">
        <h2 className="text-lg font-semibold mb-4">Customize your cookie settings</h2>
        <div className="space-y-3">
          {COOKIE_CATEGORIES.map(({ key, label, description, required }) => (
            <label key={key} className="flex items-center justify-between">
              <span>
                <strong>{label}</strong>
                <p className="text-xs text-gray-600">{description}</p>
              </span>
              <input
                type="checkbox"
                disabled={required}
                checked={localPrefs[key]}
                onChange={() => setLocalPrefs({ ...localPrefs, [key]: !localPrefs[key] })}
              />
            </label>
          ))}
        </div>
        <div className="mt-6 flex justify-between">
          <button onClick={() => setDetailsOpen(true)} className="px-4 py-2 border border-gray-300 text-stone-700 hover:border-gray-400 rounded-md text-sm">
            What do we use?
          </button>
          <div className="space-x-2">
            <button onClick={saveAndClose} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 rounded-md text-sm text-white">
              Save Preferences
            </button>
          </div>
        </div>

        {detailsOpen && <CookieDetailsModal onClose={() => setDetailsOpen(false)} />}
      </div>
    </div>
  )
}

CookieSettingsModal.propTypes = {
  onClose: PropTypes.func
} 

/**
 * Shows banner until the user makes any choice.
 * @returns {JSX.Element|null}
 */
export const CookieConsentBanner = () => {
  const { prefs, setPrefs, regionEligible, setAskOpen, askOpen } = useCookieConsent()
  const [open, setOpen] = useState(false)

  const acceptAll = () => setPrefs(COOKIE_CATEGORIES.reduce((acc, { key }) => ({ ...acc, [key]: true }), {}))

  // don’t show again if they’ve already chosen anything
  if(!askOpen) {
    if (prefs.confirmed) return null
    if (!regionEligible) return null
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[50] pb-[60px] md:pb-4 md:pr-[175px] bg-gray-800 text-white p-4 flex flex-col md:flex-row items-center justify-between space-y-2 md:space-y-0">
      <p className="text-sm md:text-base">We use 3rd-party cookies to improve your experience. You can customize what cookies you want to use.</p>
      <div className="flex space-x-2">
        <button
          onClick={() => setOpen(true)}
          className="px-4 py-2 bg-gray-300 text-stone-700 hover:bg-gray-400 rounded-md text-sm whitespace-pre"
        >Select &amp; Customize</button>
        <button
          onClick={acceptAll}
          className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 rounded-md text-sm whitespace-pre"
        >Accept All</button>
      </div>

      {(open||askOpen) && (
        <CookieSettingsModal onClose={() => {setOpen(false); setAskOpen(false)}} acceptAll={acceptAll} />
      )}
    </div>
  )
}
